import type { TFunction } from "i18next";
import type { StreamSearchParams } from "@/lib/utils/streamSearchUtils";
import type { StreamSearchCriteriaState } from "@/lib/utils/streamSearchUtils";

type PostSearchOptions = {
  groupId: number | null;
  channelId?: number | string | null;
  channelKey?: string | null;
  searchCriteriaState: StreamSearchCriteriaState;
};

type PostsHookQuery = {
  groupId: number | null;
  channelId?: number | string | null;
  channelKey?: string | null;
  search?: StreamSearchParams;
  enabled: boolean;
};

export const resolveStreamSearchPostsQuery = ({
  groupId,
  channelId,
  channelKey,
  searchCriteriaState,
}: PostSearchOptions): PostsHookQuery => {
  const { criteria, hasCriteria: hasSearchCriteria } = searchCriteriaState;
  return {
    groupId,
    channelId,
    channelKey,
    search: hasSearchCriteria ? criteria : undefined,
    enabled: hasSearchCriteria,
  };
};

export type StreamSearchPostsQuery = ReturnType<
  typeof resolveStreamSearchPostsQuery
>;

export const isStreamSearchLoadMoreAvailable = ({
  hasSearchCriteria,
  hasNextPage,
}: {
  hasSearchCriteria: boolean;
  hasNextPage: boolean;
}): boolean => hasSearchCriteria && hasNextPage;

export type SearchResultMessages = {
  invalid: string;
  loading: string;
  error: string;
  empty: string;
};

const formatSearchDateForDisplay = (value: string | undefined): string => {
  if (!value) return "";

  const datePartMatch = value.match(/^\d{4}-\d{2}-\d{2}/);
  if (datePartMatch) {
    return datePartMatch[0];
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(parsed);
};

export const resolveStudyGroupIdForSearchResults = (
  studyGroupId: number | null,
) => {
  const resolvedStudyGroupId = studyGroupId ?? undefined;
  const studyGroupIdText =
    resolvedStudyGroupId != null ? String(resolvedStudyGroupId) : undefined;

  return { resolvedStudyGroupId, studyGroupIdText };
};

export const buildSearchResultQueryText = ({
  t,
  searchParams,
  getAuthorLabel,
}: {
  t: TFunction;
  searchParams: StreamSearchParams;
  getAuthorLabel?: (authorId: string) => string | undefined;
}): string => {
  const parts: string[] = [];

  if (searchParams.keyword) {
    parts.push(searchParams.keyword);
  }

  if (searchParams.authorId) {
    const authorLabel =
      getAuthorLabel?.(searchParams.authorId) ?? searchParams.authorId;
    parts.push(`${t("streamSearch.author")}: ${authorLabel}`);
  }

  if (searchParams.from) {
    const fromText = formatSearchDateForDisplay(searchParams.from);
    if (fromText) {
      parts.push(`${t("common.startDateForFilter")}: ${fromText}`);
    }
  }

  if (searchParams.to) {
    const toText = formatSearchDateForDisplay(searchParams.to);
    if (toText) {
      parts.push(`${t("common.endDateForFilter")}: ${toText}`);
    }
  }

  return parts.join(" / ");
};

export const createSearchResultMessages = ({ t }: { t: TFunction }) =>
  ({
    invalid: t("streamSearch.requiredConditionMessage"),
    loading: t("common.searching"),
    error: t("common.errorWhileLoading"),
    empty: t("streamSearch.noResults"),
  }) as SearchResultMessages;
